import { ILibreNode } from '@/contracts/modules/libre/entities/ILibreNode'
import { ILibreTree } from '@/contracts/modules/libre/use-case/ILibreTree'
import { isStructureKind } from '@/contracts/modules/libre/enum/EnumLibreKind'

function comparePosition(a: ILibreNode, b: ILibreNode): number {
  const left = a.getPosition()
  const right = b.getPosition()
  if (left !== right) {
    return left < right ? -1 : 1
  }
  return a.getUuid() < b.getUuid() ? -1 : a.getUuid() > b.getUuid() ? 1 : 0
}

/**
 * Дерево в памяти из списка узлов. Строится один раз на версию store; все взгляды (вниз,
 * вверх, соседи, угол по тегу) считаются по нему без обращений к IndexedDB.
 * Родитель, которого нет среди читаемых узлов, считается корнем. Цикл переносов рвётся на
 * узле с наименьшим UUID в цикле: он становится корнем.
 */
export class LibreTree implements ILibreTree {
  private nodes = new Map<string, ILibreNode>()
  /** Действующий родитель после разрыва циклов и потери родителей */
  private parents = new Map<string, string | null>()
  private childrenOf = new Map<string | null, ILibreNode[]>()
  private cycleBreaks = new Set<string>()
  private hidden = new Set<string>()
  private tags = new Map<string, string[]>()
  private order: ILibreNode[] = []
  private unreadableNodes: ILibreNode[] = []

  constructor(nodes: ILibreNode[]) {
    nodes.forEach((node) => {
      if (node.isReadable()) {
        this.nodes.set(node.getUuid(), node)
      } else {
        this.unreadableNodes.push(node)
      }
    })
    this.nodes.forEach((node) => this.parents.set(node.getUuid(), this.effectiveParent(node)))
    this.nodes.forEach((node) => {
      const parent = this.parents.get(node.getUuid()) ?? null
      this.childrenOf.set(parent, [...(this.childrenOf.get(parent) ?? []), node])
    })
    this.childrenOf.forEach((list) => list.sort(comparePosition))
    this.walk(null, false, [], 0)
  }

  children(parent: string | null): ILibreNode[] {
    return (this.childrenOf.get(parent) ?? []).filter((node) => !this.hidden.has(node.getUuid()))
  }

  ancestors(uuid: string): ILibreNode[] {
    const path: ILibreNode[] = []
    let current = this.parents.get(uuid) ?? null
    while (current !== null) {
      const node = this.nodes.get(current)
      if (node === undefined) {
        break
      }
      path.unshift(node)
      current = this.parents.get(current) ?? null
    }
    return path
  }

  descendants(uuid: string): ILibreNode[] {
    const result: ILibreNode[] = []
    const collect = (parent: string): void => {
      this.children(parent).forEach((child) => {
        result.push(child)
        collect(child.getUuid())
      })
    }
    collect(uuid)
    return result
  }

  around(uuid: string, depth: number): ILibreNode[] {
    const distance = new Map<string, number>([[uuid, 0]])
    const queue = [uuid]
    while (queue.length > 0) {
      const current = queue.shift() as string
      const step = (distance.get(current) ?? 0) + 1
      if (step > depth) {
        continue
      }
      const parent = this.parents.get(current) ?? null
      const neighbours = [...this.children(current)]
      if (parent !== null && this.nodes.has(parent)) {
        neighbours.push(this.nodes.get(parent) as ILibreNode)
      }
      neighbours.forEach((node) => {
        const id = node.getUuid()
        if (!distance.has(id) && !this.hidden.has(id)) {
          distance.set(id, step)
          queue.push(id)
        }
      })
    }
    return this.order.filter((node) => node.getUuid() !== uuid && distance.has(node.getUuid()))
  }

  all(): ILibreNode[] {
    return [...this.order]
  }

  effectiveTags(uuid: string): string[] {
    return [...(this.tags.get(uuid) ?? [])]
  }

  isHidden(uuid: string): boolean {
    return this.hidden.has(uuid) || !this.nodes.has(uuid)
  }

  isCycleBreak(uuid: string): boolean {
    return this.cycleBreaks.has(uuid)
  }

  get(uuid: string): ILibreNode | null {
    return this.nodes.get(uuid) ?? null
  }

  unreadable(): ILibreNode[] {
    return [...this.unreadableNodes]
  }

  /** Родитель узла с учётом потерянных родителей и циклов */
  private effectiveParent(node: ILibreNode): string | null {
    const parent = node.getParent()
    if (parent === null || !this.nodes.has(parent)) {
      return null
    }
    const chain: string[] = []
    let current: string | null = node.getUuid()
    while (current !== null) {
      if (chain.includes(current)) {
        const cycle = chain.slice(chain.indexOf(current))
        const breakAt = cycle.reduce((min, item) => (item < min ? item : min))
        this.cycleBreaks.add(breakAt)
        return breakAt === node.getUuid() ? null : parent
      }
      chain.push(current)
      current = this.nodes.get(current)?.getParent() ?? null
      if (current !== null && !this.nodes.has(current)) {
        current = null
      }
    }
    return parent
  }

  /** Обход в порядке дерева: порядок, скрытие удалённых поддеревьев и детей листьев, действующие теги */
  private walk(parent: string | null, hiddenAbove: boolean, tagsAbove: string[], depth: number): void {
    (this.childrenOf.get(parent) ?? []).forEach((node) => {
      const uuid = node.getUuid()
      const isHidden = hiddenAbove || node.isRemoved()
      const tags = [...tagsAbove]
      node.getTags().forEach((tag) => { if (!tags.includes(tag)) tags.push(tag) })
      this.tags.set(uuid, tags)
      if (isHidden) {
        this.hidden.add(uuid)
      } else {
        this.order.push(node)
      }
      // дети листа в журнале возможны (другое устройство), но не показываются
      this.walk(uuid, isHidden || !isStructureKind(node.getKind()), tags, depth + 1)
    })
  }
}
